import ResellerDashboard from '../ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { BOOK_CALL_CALENDLY_URL } from 'src/constants';
import { ArrowUpRight, Briefcase, Headphones } from 'lucide-react';
import SupportCard from './SupportCard';
import { Button, Tooltip, Typography } from 'antd';
import { useEffect, useRef } from 'react';
import { hide, show } from '@intercom/messenger-js-sdk';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData, getResellerTrialStatus } from '@/store/reseller/selector';
import ResellerTour from '../ResellerTour/ResellerTour';
import { getResellerTourState } from '@/store/resellerTour/selector';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateTourStatus } from '@/api/reseller';
import { setTour } from '@/store/resellerTour/slice';
import { AppRoutes } from 'src/enums/ERoutes';

const { Title, Paragraph } = Typography;

const ResellerContactSupport = () => {
	const isResellerOnTrial = useAppSelector(getResellerTrialStatus);
	const resellerTour = useAppSelector(getResellerTourState);
	const resellerData = useAppSelector(getResellerData);
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const supportTourRef1 = useRef<HTMLDivElement>(null);
	const supportTourRef2 = useRef<HTMLDivElement>(null);

	const updateToursMutation = useMutation({
		mutationFn: updateTourStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	useEffect(() => {
		if (!resellerData.emailAddress || resellerTour.open) {
			return;
		}

		if (!resellerData.tours?.contact_support) {
			updateToursMutation.mutate('contact_support');
			dispatch(
				setTour({
					open: true,
					currentStep: 0,
					returnTo: AppRoutes.RESELLER_CONTACT_SUPPORT,
				}),
			);
		}
	}, [resellerData.emailAddress, resellerTour.open]);

	const supportTourSteps = [
		{
			title: <h1 className="font-bold text-2xl">Book a Product call with your CSM</h1>,
			description: (
				<p className="text-base">
					Have questions or need help with your AI receptionists? Schedule a call with your Customer
					Success Manager and we'll take care of it together.
				</p>
			),
			target: supportTourRef1.current as HTMLElement,
		},
		{
			title: <h1 className="font-bold text-2xl">Book a Business Call with Our Director</h1>,
			description: (
				<p className="text-base">
					Have questions about strategy, pricing, or scaling your business? Once you reach 7 paid
					accounts, you can book a direct call with the Director of our program.
				</p>
			),
			target: supportTourRef2.current as HTMLElement,
		},
		{
			title: <h1 className="font-bold text-2xl">24/7 Chat Support</h1>,
			description: (
				<p className="text-base">
					Get instant help with our AI chat anytime, and live support during EST business hours.
					Have a question? Just drop us a message!
				</p>
			),
		},
	];

	useEffect(() => {
		if (!resellerTour.open) {
			show();
		} else {
			hide();
		}
	}, [resellerTour.open]);

	const openCalendlyLink = (type: string) => {
		let url = '';

		switch (type) {
			case 'technical':
				url = 'https://calendly.com/d/cmq2-bzn-bth/ai-front-desk-onboarding-setup-help';
				break;
			case 'business':
				url = 'https://calendly.com/d/cqb8-y7w-74c/ai-front-desk-white-label-discovery-call';
				break;
			default:
				url = BOOK_CALL_CALENDLY_URL;
		}

		window.open(url, '_blank');
	};

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.CONTACT_SUPPORT}>
			<div className="p-4">
				<div className="w-full max-w-6xl mx-auto px-4 py-10">
					<div className="text-center mb-16">
						<Title className="text-5xl font-bold mb-4">How can we help you?</Title>
						<Paragraph className="text-gray-600 text-lg max-w-3xl mx-auto">
							We're here to provide support and assistance when you need it. Choose the option that
							best suits your needs.
						</Paragraph>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div ref={supportTourRef1}>
							<SupportCard
								icon={<Headphones size={32} color="#4096ff" />}
								title="Technical Call"
								description="Need help with technical issues? Schedule a call with our technical support team for personalized assistance."
								buttonColor="blue"
							>
								<Button
									type="primary"
									size="large"
									icon={<ArrowUpRight size={18} />}
									onClick={() => openCalendlyLink('technical')}
									className={
										'flex items-center mt-4 bg-blue-500 hover:bg-blue-600 disabled:!pointer-events-none'
									}
								>
									Schedule Technical Call
								</Button>
							</SupportCard>
						</div>

						<div ref={supportTourRef2}>
							<SupportCard
								icon={<Briefcase size={32} color="#722ed1" />}
								title="Business Call"
								description="Have questions about billing, plans or business inquiries? Our business team is ready to assist you."
								buttonColor="purple"
							>
								{isResellerOnTrial || resellerData.totalInboundAgents < 7 ? (
									<Tooltip title="You will be able to book a business call once you are out of your trial period.">
										<Button
											type="primary"
											disabled
											size="large"
											icon={<ArrowUpRight size={18} />}
											onClick={() => openCalendlyLink('business')}
											className={
												'flex items-center mt-4 bg-blue-500 hover:bg-blue-600 disabled:hover:!bg-gray-200 disabled:hover:!border-gray-200'
											}
										>
											Schedule Business Call
										</Button>
									</Tooltip>
								) : (
									<Button
										type="primary"
										size="large"
										icon={<ArrowUpRight size={18} />}
										onClick={() => openCalendlyLink('business')}
										className={'flex items-center mt-4 bg-blue-500 hover:bg-blue-600'}
									>
										Schedule Business Call
									</Button>
								)}
							</SupportCard>
						</div>
					</div>
				</div>
			</div>
			<ResellerTour tourSteps={supportTourSteps} />
		</ResellerDashboard>
	);
};

export default ResellerContactSupport;
